import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

export interface ModuleNavItem {
  to: string;
  labelKey: string;
  end?: boolean;
  /** CASL subject for permission filtering, e.g. 'scouting:fieldMode' */
  subject?: string;
  /** i18n key for the group this item belongs to, e.g. 'nav.groups.operations' */
  group?: string;
}

interface ModuleNavContextType {
  moduleTitle: string | null;
  navItems: ModuleNavItem[] | null;
  setModule: (title: string | null, items: ModuleNavItem[] | null) => void;
}

const ModuleNavContext = createContext<ModuleNavContextType>({
  moduleTitle: null,
  navItems: null,
  setModule: () => {},
});

export const ModuleNavProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [moduleTitle, setModuleTitle] = useState<string | null>(null);
  const [navItems, setNavItems] = useState<ModuleNavItem[] | null>(null);

  const setModule = useCallback((title: string | null, items: ModuleNavItem[] | null) => {
    setModuleTitle(prev => prev === title ? prev : title);
    setNavItems(prev => {
      if (prev === items) return prev;
      if (!prev || !items || prev.length !== items.length) return items;
      for (let i = 0; i < items.length; i++) {
        if (
          items[i].to !== prev[i].to ||
          items[i].labelKey !== prev[i].labelKey ||
          items[i].group !== prev[i].group
        ) return items;
      }
      return prev;
    });
  }, []);

  const value = useMemo(() => ({ 
    moduleTitle, 
    navItems, 
    setModule 
  }), [moduleTitle, navItems, setModule]);

  return (
    <ModuleNavContext.Provider value={value}>
      {children}
    </ModuleNavContext.Provider>
  );
};

export const useModuleNav = () => useContext(ModuleNavContext);
